import {
  RecommendationContentTypeDto,
  RemediationActivityTypeDto,
  StatusWithDueDate
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import {
  ELSButton,
  ELSIcon
} from '../../components/els.components';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  getGoalBreadcrumbs,
  getGoalByVtwId
} from '../../utilities/remediation.utilities';
import { convertDateToMMDDYYYY } from './remediation-student-activity.utilities';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { defaultActivityLogState } from './ActivityLog.component';

export type ActivityLogFilterPillsProps = {
  selectedActivityTypes: RemediationActivityTypeDto[];
  selectedContentTypes: RecommendationContentTypeDto[];
  selectedStatuses: StatusWithDueDate[];
  selectedGoalId: string;
  startDate: Date;
  endDate: Date;
  assessmentGoals: AssessmentGoalDto[];
  handleApplyFilters: (props: {
    selectedActivityTypes: RemediationActivityTypeDto[];
    selectedContentTypes: RecommendationContentTypeDto[];
    selectedStatuses: StatusWithDueDate[];
    selectedGoalId: string;
    startDate: Date;
    endDate: Date;
  }) => void;
};

export const ActivityLogFilterPills = (props: ActivityLogFilterPillsProps): JSX.Element => {

  const handleCancelSelectedActivityType = (activityType: RemediationActivityTypeDto) => {
    const newSelectedActivityTypes = props.selectedActivityTypes.filter((type) => type !== activityType);
    props.handleApplyFilters({
      selectedActivityTypes: newSelectedActivityTypes,
      selectedContentTypes: props.selectedContentTypes,
      selectedStatuses: props.selectedStatuses,
      selectedGoalId: props.selectedGoalId,
      startDate: props.startDate,
      endDate: props.endDate,
    });
  };

  const handleCancelSelectedContentType = (contentType: RecommendationContentTypeDto) => {
    const newSelectedContentTypes = props.selectedContentTypes.filter((type) => type !== contentType);
    props.handleApplyFilters({
      selectedActivityTypes: props.selectedActivityTypes,
      selectedContentTypes: newSelectedContentTypes,
      selectedStatuses: props.selectedStatuses,
      selectedGoalId: props.selectedGoalId,
      startDate: props.startDate,
      endDate: props.endDate,
    });
  };

  const handleCancelSelectedStatus = (status: StatusWithDueDate) => {
    const newSelectedStatuses = props.selectedStatuses.filter((s) => s !== status);
    props.handleApplyFilters({
      selectedActivityTypes: props.selectedActivityTypes,
      selectedContentTypes: props.selectedContentTypes,
      selectedStatuses: newSelectedStatuses,
      selectedGoalId: props.selectedGoalId,
      startDate: props.startDate,
      endDate: props.endDate,
    });
  };

  const handleCancelSelectedGoal = () => {
    props.handleApplyFilters({
      selectedActivityTypes: props.selectedActivityTypes,
      selectedContentTypes: props.selectedContentTypes,
      selectedStatuses: props.selectedStatuses,
      selectedGoalId: null,
      startDate: props.startDate,
      endDate: props.endDate,
    });
  };

  const handleCancelSelectedDateRange = () => {
    props.handleApplyFilters({
      selectedActivityTypes: props.selectedActivityTypes,
      selectedContentTypes: props.selectedContentTypes,
      selectedStatuses: props.selectedStatuses,
      selectedGoalId: props.selectedGoalId,
      startDate: null,
      endDate: null,
    });
  };

  const filterPill = (title: string, onClose: () => void) => {
    return (
      <FlexLayout
        classes={['c-els-pill c-nra-activity-log__filter-pill']}
        modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}
      >
        <FlexItem>
          {title}
        </FlexItem>
        <FlexItem>
          <button
            type="button"
            className="c-nra-activity-log__filter-pill-close-button u-els-anchorize u-els-debuttonize"
            onClick={onClose}
          >
            <ELSIcon name="close" size="1o2" />
            <span className="u-els-hide-visually">{`Cancel ${title} filter setting`}</span>
          </button>
        </FlexItem>
      </FlexLayout>
    );
  };

  const getDateRangeTitle = (startDate: Date, endDate: Date): string => {
    if (!startDate && !endDate) {
      return '';
    }

    if (startDate && endDate) {
      return `Date Submitted: ${convertDateToMMDDYYYY(startDate)} - ${convertDateToMMDDYYYY(endDate)}`;
    }

    if (startDate) {
      return `Date Submitted: From ${convertDateToMMDDYYYY(startDate)}`;
    }

    return `Date Submitted: Until ${convertDateToMMDDYYYY(endDate)}`;
  };

  const handleResetAllFilters = () => {
    props.handleApplyFilters(defaultActivityLogState);
  };

  const areFiltersApplied = (): boolean => {
    return !!(props.selectedGoalId
      || props.startDate
      || props.endDate
      || props.selectedStatuses.length > 0
      || props.selectedContentTypes.length > 0
      || props.selectedActivityTypes.length > 0);
  };

  const getSelectedGoalName = () => {
    const goal = getGoalByVtwId(props.assessmentGoals, props.selectedGoalId);
    if (!goal) {
      return ''
    }
    const breadcrumbs = getGoalBreadcrumbs(goal);
    return breadcrumbs.pathFullDisplay || '';
  }

  return (
    <div>
      <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.WRAP]}>
        {
          props.selectedActivityTypes.length > 0 &&
          props.selectedActivityTypes.map((activityType) => (
            <div key={activityType}>
              {filterPill(
                `Activity Type: ${ActivityTypeConfigMap[activityType].title}`,
                () => handleCancelSelectedActivityType(activityType)
              )}
            </div>
          ))
        }
        {
          props.selectedContentTypes.length > 0 &&
          props.selectedContentTypes.map((contentType) => (
            <div key={contentType}>
              {filterPill(
                `Content Type: ${RemediationContentTypeConfigMap[contentType].displayName}`,
                () => handleCancelSelectedContentType(contentType)
              )}
            </div>
          ))
        }
        {
          props.selectedStatuses.length > 0 &&
          props.selectedStatuses.map((status) => (
            <div key={status}>
              {filterPill(
                `Status: ${StatusDisplayMap[status]}`,
                () => handleCancelSelectedStatus(status)
              )}
            </div>
          ))
        }
        {
          props.selectedGoalId &&
          filterPill(
            `Activity Name: ${getSelectedGoalName()}`,
            handleCancelSelectedGoal
          )
        }
        {
          (props.startDate || props.endDate) &&
          filterPill(
            getDateRangeTitle(props.startDate, props.endDate),
            handleCancelSelectedDateRange
          )
        }
        {
          areFiltersApplied() && (
            <ELSButton
              type={ELSButtonType.SECONDARY}
              size={ELSButtonSize.X_SMALL}
              onClick={handleResetAllFilters}
            >
              <span className="u-els-bold c-nra-activity-log__secondary-btn-text">
                Reset all filters
              </span>
            </ELSButton>
          )
        }
      </FlexLayout>
    </div>
  );
};
